import { buildApiUrl, getAuthHeaders } from '@/config/api';
import { Organization } from '@/types/auth';

interface UpdateOrganizationData {
  name?: string;
  domain?: string;
  defaultCurrency?: string;
  settings?: any;
}

interface UpdateCurrencyData {
  defaultCurrency: string;
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

const API_BASE_URL = buildApiUrl('/organization');

class OrganizationService {
  // Get organization details
  async getOrganization(): Promise<Organization> {
    const response = await fetch(`${API_BASE_URL}`, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `Failed to get organization: ${response.status}`);
    }

    const result: ApiResponse<Organization> = await response.json();
    if (!result.success || !result.data) {
      throw new Error(result.error || 'Failed to get organization');
    }

    return result.data;
  }

  // Update organization
  async updateOrganization(updateData: UpdateOrganizationData): Promise<Organization> {
    const response = await fetch(`${API_BASE_URL}`, {
      method: 'PUT',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updateData),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `Failed to update organization: ${response.status}`);
    }

    const result: ApiResponse<Organization> = await response.json();
    if (!result.success || !result.data) {
      throw new Error(result.error || 'Failed to update organization');
    }

    return result.data;
  }

  // Update default currency
  async updateDefaultCurrency(defaultCurrency: string): Promise<Organization> {
    const response = await fetch(`${API_BASE_URL}/currency`, {
      method: 'PUT',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ defaultCurrency }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `Failed to update default currency: ${response.status}`);
    }

    const result: ApiResponse<Organization> = await response.json();
    if (!result.success || !result.data) {
      throw new Error(result.error || 'Failed to update default currency');
    }

    return result.data;
  }
}

export const organizationService = new OrganizationService();
export default organizationService;
